/* ═══════════════════════════════════════════════════════════
   PEAKVIEW ALPINE — SUMMIT SERIES I (collection page)

   Vanilla, dependency-free, progressive enhancement only.
   No GSAP / ScrollTrigger / Lenis / Three, no canvas, no WebGL, no
   requestAnimationFrame loop, no scroll pinning, no parallax, no
   counters. assets/main.js is never imported — which also means the
   generic .access-form handler in main.js can never run on this route.

   Everything below is additive: with this file absent, blocked or
   throwing, the page still renders complete and fully readable.
   ═══════════════════════════════════════════════════════════ */
(function () {
  "use strict";

  /* Both sections emit this script so the page still works if either one is
     removed in the editor. The guard makes the second execution a no-op. */
  if (window.__pvSummitBound) return;
  window.__pvSummitBound = true;

  var root = document.documentElement;
  var reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  var hasIO = typeof IntersectionObserver !== "undefined";

  /* The shared header/footer brand mark is mounted by assets/site-chrome.js,
     which layout/theme.liquid loads on every non-index route. This file stays
     page-specific: edition reveals, the edition rail, and Theme Editor support. */

  /* observers are parked on the node they belong to, so a Theme Editor
     section unload can disconnect exactly its own and nothing else */
  function track(node, observer) {
    if (!node.__pvObs) node.__pvObs = [];
    node.__pvObs.push(observer);
  }

  function release(node) {
    var list = node && node.__pvObs;
    if (!list) return;
    for (var i = 0; i < list.length; i++) list[i].disconnect();
    node.__pvObs = null;
  }

  function markAll(scope) {
    var els = scope.querySelectorAll("[data-pv-reveal]");
    for (var i = 0; i < els.length; i++) {
      els[i].setAttribute("data-pv-ready", "1");
      els[i].classList.add("is-in");
    }
  }

  /* ── one-time entry reveal ─────────────────── */
  function initReveals(scope, instant) {
    var els = scope.querySelectorAll("[data-pv-reveal]");
    if (!els.length) return;

    if (instant || reduced || !hasIO) {
      markAll(scope);
      return;
    }

    var io = new IntersectionObserver(
      function (entries, obs) {
        for (var i = 0; i < entries.length; i++) {
          if (!entries[i].isIntersecting) continue;
          entries[i].target.classList.add("is-in");
          obs.unobserve(entries[i].target);
        }
      },
      { rootMargin: "0px 0px -6% 0px", threshold: 0.06 }
    );

    for (var j = 0; j < els.length; j++) {
      if (els[j].getAttribute("data-pv-ready")) continue;
      els[j].setAttribute("data-pv-ready", "1");
      io.observe(els[j]);
    }
    track(scope, io);
  }

  /* ── rail active state + mobile current-edition label ── */
  function initRail(scope) {
    var index = scope.querySelector ? scope.querySelector("[data-pv-index]") : null;
    if (!index && scope.nodeType === 1 && scope.matches && scope.matches("[data-pv-index]")) {
      index = scope;
    }
    if (!index || index.getAttribute("data-pv-ready")) return;
    index.setAttribute("data-pv-ready", "1");

    var chapters = index.querySelectorAll("[data-pv-chapter]");
    var links = index.querySelectorAll("[data-pv-rail-link]");
    if (!chapters.length || !links.length) return;

    var barNum = index.querySelector("[data-pv-bar-num]");
    var barName = index.querySelector("[data-pv-bar-name]");

    function setActive(key, num, name) {
      for (var i = 0; i < links.length; i++) {
        var on = links[i].getAttribute("data-pv-rail-link") === key;
        links[i].classList.toggle("is-active", on);
        if (on) links[i].setAttribute("aria-current", "location");
        else links[i].removeAttribute("aria-current");
      }
      if (barNum) barNum.textContent = num;
      if (barName) barName.textContent = name;
    }

    /* read the label straight off the rail so the script never holds its own
       copy of an edition name */
    function labelFor(key) {
      for (var i = 0; i < links.length; i++) {
        if (links[i].getAttribute("data-pv-rail-link") !== key) continue;
        var n = links[i].querySelector(".pv-rail__num");
        var m = links[i].querySelector(".pv-rail__name");
        return { num: n ? n.textContent : "", name: m ? m.textContent : "" };
      }
      return { num: "", name: "" };
    }

    function apply(key) {
      var label = labelFor(key);
      setActive(key, label.num, label.name);
    }

    var first = chapters[0].getAttribute("data-pv-chapter");
    apply(first);

    if (!hasIO) return;

    /* a thin band across the middle of the viewport decides which chapter is
       current — event driven, no scroll listener, no per-frame work */
    var io = new IntersectionObserver(
      function (entries) {
        for (var i = 0; i < entries.length; i++) {
          if (!entries[i].isIntersecting) continue;
          apply(entries[i].target.getAttribute("data-pv-chapter"));
        }
      },
      { rootMargin: "-45% 0px -45% 0px", threshold: 0 }
    );
    for (var c = 0; c < chapters.length; c++) io.observe(chapters[c]);
    track(index, io);
  }

  function init(scope, instant) {
    initReveals(scope, instant);
    initRail(scope);
  }

  /* ── boot ──────────────────────────────────── */
  try {
    init(document, false);
    root.classList.add("pv-live");
  } catch (err) {
    /* never leave content stranded behind a pre-reveal state */
    root.classList.add("pv-no-anim");
  }

  /* ── Shopify Theme Editor (design mode only) ── */
  /* Narrow by intent: re-init the reloaded section, disconnect only that
     section's observers on unload, scroll the selected section into view.
     Reveals are applied instantly while editing rather than replayed. */
  if (window.Shopify && window.Shopify.designMode) {
    document.addEventListener("shopify:section:load", function (e) {
      try {
        init(e.target, true);
      } catch (err) {
        root.classList.add("pv-no-anim");
      }
    });

    document.addEventListener("shopify:section:unload", function (e) {
      release(e.target);
      var index = e.target.querySelector ? e.target.querySelector("[data-pv-index]") : null;
      if (index) release(index);
    });

    document.addEventListener("shopify:section:select", function (e) {
      if (e.target && e.target.scrollIntoView) {
        e.target.scrollIntoView({ behavior: reduced ? "auto" : "smooth", block: "center" });
      }
    });
  }
})();
